import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, TrendingDown, DollarSign, Users, Calendar, Package,
  BarChart3, Loader2, ArrowUpRight, ArrowDownRight, Star, Repeat,
  Target, MapPin, Clock, PieChart
} from "lucide-react";
import AIHealthWidget from "./AIHealthWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ icon: Icon, label, value, sub, trend, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center">
        <Icon size={16} className="text-[#DC283C]" />
      </div>
      {trend !== undefined && (
        <span className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${trend >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
          {trend >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-[10px] text-[#94a3b8] font-medium mt-0.5">{label}</p>
    {sub && <p className="text-[9px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

const BarChartSimple = ({ data, valueKey, labelKey, color = "#DC283C", height = 140 }) => {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((d, i) => {
        const h = Math.max((d[valueKey] / max) * 100, 3);
        return (
          <div key={d[labelKey] ?? `bar-${i}`} className="flex-1 flex flex-col items-center gap-1" title={`${d[labelKey]}: ${d[valueKey]?.toLocaleString?.() || d[valueKey]}`}>
            <span className="text-[7px] text-[#94a3b8] font-medium">{d[valueKey] > 0 ? (d[valueKey] >= 1000 ? `${Math.round(d[valueKey]/1000)}k` : d[valueKey]) : ""}</span>
            <div className="w-full rounded-t-sm transition-all hover:opacity-80" style={{ height: `${h}%`, background: color, minHeight: 3 }} />
            <span className="text-[7px] text-[#94a3b8] truncate w-full text-center">{d[labelKey]?.slice?.(-5) || d[labelKey]}</span>
          </div>
        );
      })}
    </div>
  );
};

const SmartAnalyticsTab = ({ auth, authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/analytics/dashboard`);
      setData(r.data);
    } catch (e) { console.error("Analytics fetch error:", e); }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#DC283C]" size={28} /></div>;
  if (!data) return <p className="text-sm text-[#94a3b8] text-center py-20">Failed to load analytics</p>;

  const ov = data.overview;

  return (
    <div className="space-y-6" data-testid="smart-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1E1B4B]">Smart Analytics</h2>
          <p className="text-xs text-[#94a3b8]">Revenue, trends & customer insights</p>
        </div>
        <button onClick={fetchData} className="px-3 py-1.5 rounded-lg text-xs bg-white border border-gray-200 text-[#475569] hover:bg-gray-50" data-testid="refresh-analytics">
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Stat icon={DollarSign} label="Total Revenue" value={`Rs ${ov.total_revenue?.toLocaleString()}`} trend={ov.revenue_growth_pct} sub={`This month: Rs ${ov.this_month_revenue?.toLocaleString()}`} />
        <Stat icon={Calendar} label="Total Bookings" value={ov.total_bookings} sub={`${ov.confirmed_bookings} confirmed, ${ov.cancelled_bookings} cancelled`} />
        <Stat icon={BarChart3} label="Avg Booking Value" value={`Rs ${ov.avg_booking_value?.toLocaleString()}`} />
        <Stat icon={Users} label="Customers" value={ov.total_customers} sub={`${ov.repeat_customers} repeat (${ov.repeat_rate}%)`} />
        <Stat icon={Target} label="Lead Conversion" value={`${ov.lead_conversion_pct}%`} sub={`${ov.total_leads} total leads`} />
      </div>

      {/* AI Health (auto-refresh 60s) */}
      <AIHealthWidget authAxios={authAxios} />

      {/* Revenue Trend + Monthly */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-1">Daily Revenue (Last 30 Days)</h3>
          <p className="text-[10px] text-[#94a3b8] mb-3">Rs per day</p>
          <BarChartSimple data={data.revenue_trend || []} valueKey="revenue" labelKey="date" color="#DC283C" height={130} />
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-1">Monthly Trend (6 Months)</h3>
          <p className="text-[10px] text-[#94a3b8] mb-3">Revenue & bookings per month</p>
          <BarChartSimple data={data.monthly_trend || []} valueKey="revenue" labelKey="month" color="#F0DC00" height={130} />
          <div className="flex gap-3 mt-2">
            {(data.monthly_trend || []).map((m, i) => (
              <div key={m.month || `mt-${i}`} className="flex-1 text-center">
                <p className="text-[8px] text-[#94a3b8]">{m.bookings} bookings</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Package + Occasion breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Package Breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Package size={14} className="text-[#DC283C]" /> Package Breakdown</h3>
          <div className="space-y-3">
            {(data.package_breakdown || []).map(p => {
              const pct = Math.round((p.count / Math.max(ov.confirmed_bookings, 1)) * 100);
              const colors = { Basic: "#10B981", Classic: "#3B82F6", Premium: "#7C3AED", Luxury: "#DC283C", Custom: "#F97316" };
              return (
                <div key={p.package}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#1E1B4B]">{p.package}</span>
                    <span className="text-[10px] text-[#94a3b8]">{p.count} ({pct}%) — Rs {p.revenue?.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: colors[p.package] || "#94a3b8" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Occasion Breakdown */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><PieChart size={14} className="text-[#F0DC00]" /> Occasions</h3>
          <div className="space-y-2.5">
            {(data.occasion_breakdown || []).slice(0, 8).map((o, i) => {
              const pct = Math.round((o.count / Math.max(ov.confirmed_bookings, 1)) * 100);
              const colors = ["#DC283C", "#F0DC00", "#7C3AED", "#10B981", "#3B82F6", "#F97316", "#E83E8C", "#6366F1"];
              return (
                <div key={o.occasion} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: colors[i % colors.length] }} />
                  <span className="text-xs text-[#475569] flex-1">{o.occasion}</span>
                  <span className="text-[10px] font-bold text-[#1E1B4B]">{o.count}</span>
                  <span className="text-[9px] text-[#94a3b8]">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Busiest Days Heatmap */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Calendar size={14} className="text-[#7C3AED]" /> Busiest Days</h3>
          <div className="grid grid-cols-7 gap-1.5">
            {(data.busiest_days || []).map(d => {
              const max = Math.max(...(data.busiest_days || []).map(x => x.bookings), 1);
              const intensity = d.bookings / max;
              return (
                <div key={d.day} className="text-center">
                  <p className="text-[9px] text-[#94a3b8] mb-1 font-medium">{d.day}</p>
                  <div className="w-full aspect-square rounded-lg flex items-center justify-center transition-colors"
                    style={{ background: `rgba(220,40,60,${0.1 + intensity * 0.7})` }}>
                    <span className={`text-[11px] font-bold ${intensity > 0.5 ? "text-white" : "text-[#DC283C]"}`}>{d.bookings}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Time Slots */}
          <h4 className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wider mt-4 mb-2">Popular Time Slots</h4>
          <div className="flex flex-wrap gap-1.5">
            {(data.time_slots || []).slice(0, 6).map(t => (
              <span key={t.time} className="px-2 py-1 rounded-full bg-[#F0DC00]/15 text-[10px] font-medium text-[#1E1B4B] flex items-center gap-1">
                <Clock size={9} /> {t.time} ({t.bookings})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Area Heatmap + Top Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Area Heatmap */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><MapPin size={14} className="text-[#10B981]" /> Top Areas</h3>
          <div className="space-y-2">
            {(data.area_heatmap || []).slice(0, 10).map((a, i) => {
              const max = Math.max(...(data.area_heatmap || []).map(x => x.bookings), 1);
              return (
                <div key={a.area} className="flex items-center gap-2">
                  <span className="w-5 text-[10px] text-[#94a3b8] font-bold">#{i+1}</span>
                  <span className="text-xs text-[#475569] flex-1 truncate">{a.area}</span>
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#10B981] rounded-full" style={{ width: `${(a.bookings / max) * 100}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-[#1E1B4B] w-6 text-right">{a.bookings}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Customers (LTV) */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-1 flex items-center gap-2"><Star size={14} className="text-[#F0DC00]" /> Top Customers (LTV)</h3>
          <p className="text-[10px] text-[#94a3b8] mb-3">{ov.repeat_rate}% repeat rate</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-1.5 text-[9px] text-[#94a3b8] font-semibold">Customer</th>
                  <th className="text-right py-1.5 text-[9px] text-[#94a3b8] font-semibold">Bookings</th>
                  <th className="text-right py-1.5 text-[9px] text-[#94a3b8] font-semibold">Total Spent</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_customers || []).slice(0, 10).map((c, i) => (
                  <tr key={c.phone || `cust-${i}`} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-1.5">
                      <p className="font-medium text-[#1E1B4B]">{c.name || "—"}</p>
                      <p className="text-[9px] text-[#94a3b8]">{c.phone}</p>
                    </td>
                    <td className="text-right">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${c.bookings > 1 ? "bg-[#F0DC00]/20 text-[#1E1B4B]" : "bg-gray-100 text-[#475569]"}`}>
                        {c.bookings > 1 && <Repeat size={8} />} {c.bookings}
                      </span>
                    </td>
                    <td className="text-right font-bold text-[#1E1B4B]">Rs {c.total_spent?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartAnalyticsTab;
